import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { TooltipButton } from './TooltipButton';
import './onboarding.css';

/** One thing to check, named for where it is answered ("Bill To", "Line 45") with its reason. */
export type ToCheckItem = { readonly place: string; readonly reason: string };

/** Where a review reason is answered, in the words the panel itself uses. */
export function reviewItemPlace(reason: {
  documentType?: boolean;
  boundary?: boolean;
  fieldName?: string;
  lineRow?: number;
  table?: boolean;
}): string {
  if (reason.documentType) return 'Document type';
  if (reason.boundary) return 'Document start';
  if (reason.lineRow) return `Line ${reason.lineRow}`;
  if (reason.fieldName) return reason.fieldName;
  if (reason.table) return 'Line items';
  return 'Document';
}

/** The reason without its place repeated in front of it ("Bill To: …" under "Bill To"). */
function reasonWithoutPlace(item: ToCheckItem) {
  const { place, reason } = item;
  if (!reason.toLowerCase().startsWith(place.toLowerCase())) return reason;
  const rest = reason
    .slice(place.length)
    .replace(/^\s*[:·\-–]\s*/u, '')
    .trim();
  return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : reason;
}

/**
 * Why the document waits, in words, over the panel, in the main window and a moved fields window
 * alike: only while nothing on it is named to check (those are counted on the Fields heading).
 */
export function WaitingLine({
  waiting,
}: {
  /** whyDocumentWaits. */
  waiting: string | null | undefined;
}) {
  return waiting ? (
    <p className="review-waiting-why" role="note">
      <span className="review-waiting-dot" aria-hidden="true" />
      <span>{waiting}</span>
    </p>
  ) : null;
}

/**
 * The Fields heading, in the main window and a moved fields window alike.
 *
 * While the document has things to check, its right end says "3 to check" in amber, with no box
 * (owner, 2026-10-30: the count sits on the list it counts, not over the panel as a button). It
 * opens the list of them by name, each with its reason, and a row goes to its place; F6 goes to
 * the next one (owner, 2026-10-15: the mockup names them, answering "and 4 more reasons" in
 * place, Paige review pane concerns, 2026-10-13). Each still says its reason where it is
 * answered. Under it, only a reason no place answers ("You put it back in review.").
 */
export function FieldsHeading({
  items,
  note,
  onGo,
}: {
  /** The things to check (reasonsToCheck), in order; empty while the document is not held. */
  items: readonly ToCheckItem[];
  /** waitingNote. */
  note?: string | null;
  /** Go to the thing to check at this index, as F6 would. */
  onGo: (index: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const toCheck = items.length > 0;
  return (
    <>
      <div className="field-workbench-fields-heading">
        <strong>Fields</strong>
        {toCheck && (
          <TooltipButton
            type="button"
            className="to-check-next"
            tooltip={
              open
                ? 'Hide the list · F6 goes to the next one'
                : 'Show what to check · F6 goes to the next one'
            }
            aria-label={`Needs review, ${items.length} to check. ${open ? 'Hide' : 'Show'} the list`}
            aria-expanded={open}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setOpen((value) => !value)}
          >
            <span className="review-waiting-dot" aria-hidden="true" />
            <span className="to-check-count">{items.length} to check</span>
            <ChevronDown size={15} aria-hidden="true" className={open ? 'open' : undefined} />
          </TooltipButton>
        )}
      </div>
      {toCheck && open && (
        <ol className="to-check-list" aria-label="Things to check">
          {items.map((item, index) => (
            <li key={`${index}-${item.place}`}>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onGo(index)}
                aria-label={`${item.place}: ${reasonWithoutPlace(item)}. Go there`}
              >
                <strong>{item.place}</strong>
                <span>{reasonWithoutPlace(item)}</span>
                <ChevronRight size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ol>
      )}
      {toCheck && note && (
        <p className="to-check-note" role="note">
          {note}
        </p>
      )}
    </>
  );
}
